import type { SxProps, Theme } from "@mui/material";

type GridContainerVariant =
	| "autoheight"
	| "csv-import-preview"
	| "dock"
	| "fit-height"
	| "flex-fill"
	| "fullscreen"
	| "header-title"
	| "kit-building-kits"
	| "kit-building-skus"
	| "mass-update"
	| "modal"
	| "modal-stepper"
	| "modal-tab"
	| "normal"
	| "recipe-details"
	| "card-tab" // Card view with tab inside
	| "card-tab-header"
	| "restock-loadlist"
	| "restock-order"
	| "settings-page"
	| "staging-flow"
	| "tab-card" // Card view with tab outside
	| "tab";

type GridInnerVariant =
	| "csv-import-preview"
	| "dock"
	| "fit-height"
	| "flex-fill"
	| "header-title"
	| "kit-building-kits"
	| "kit-building-skus"
	| "mass-update"
	| "modal"
	| "modal-stepper"
	| "modal-tab"
	| "normal"
	| "recipe-details"
	| "card-tab"
	| "card-tab-header"
	| "restock-loadlist"
	| "settings-page"
	| "staging-flow"
	| "tab-card"
	| "tab";

export const gridContainerSx: Record<GridContainerVariant, SxProps<Theme>> = {
	autoheight: {
		height: "unset"
	},
	"csv-import-preview": {
		height: "100%"
	},
	dock: {
		"& .ag-status-bar": {
			pr: "60px",
			zIndex: 2
		},
		display: "flex",
		flex: 1,
		flexDirection: "column",
		minHeight: 0,
		minWidth: "100%",
		position: "relative",
		width: "auto",
		zIndex: 1
	},
	// Emotion hoists @media after flat declarations, so a bare height cannot override normal's narrow-viewport heights.
	"fit-height": {
		"@media (max-width: 752px)": {
			height: "100%"
		},
		"@media (max-width: 900px)": {
			height: "100%"
		},
		height: "100%"
	},
	fullscreen: {
		height: "100%",
		left: 0,
		overflowX: "hidden",
		position: "fixed",
		top: 0,
		width: "100%",
		zIndex: 3000
	},
	"flex-fill": {
		flex: "1 1 0",
		minHeight: 0
	},
	"header-title": {
		height: "calc(100vh - 350px)"
	},
	"kit-building-kits": {
		height: "100%"
	},
	"kit-building-skus": {
		height: "100%"
	},
	"mass-update": {
		height: "calc(100vh - 350px)"
	},
	modal: {
		height: "calc(100vh - 146px)"
	},
	"modal-stepper": {
		height: "calc(100vh - 220px)"
	},
	"modal-tab": {
		height: "calc(100vh - 240px)"
	},
	normal: {
		"@media (max-width: 752px)": {
			height: "calc(100vh - 130px)",
			overflowX: "hidden",
			width: "100%"
		},
		"@media (max-width: 900px)": {
			height: "calc(100vh - 180px)"
		},
		"& .ag-status-bar": {
			pr: "60px",
			zIndex: 2
		},
		display: "block",
		height: "calc(100vh - 120px)",
		minWidth: "100%",
		position: "relative",
		width: "auto",
		zIndex: 1
	},
	"recipe-details": {
		height: "calc(100vh - 758px)"
	},
	"card-tab": {
		height: "calc(100vh - 416px)"
	},
	"card-tab-header": {
		height: "calc(100vh - 440px)"
	},
	"restock-loadlist": {
		height: "calc(100vh - 170px)"
	},
	"restock-order": {
		height: "calc(100vh - 145px)"
	},
	"settings-page": {
		"& .ag-status-bar": {
			pr: "60px",
			zIndex: 2
		},
		display: "flex",
		flex: 1,
		flexDirection: "column",
		minHeight: 0,
		minWidth: "100%",
		position: "relative",
		width: "auto",
		zIndex: 1
	},
	"staging-flow": {
		display: "block",
		height: "100%",
		minWidth: "100%",
		position: "relative",
		width: "auto",
		zIndex: 1
	},
	"tab-card": {
		height: "calc(100vh - 290px)"
	},
	tab: {
		height: "calc(100vh - 288px)"
	}
};

export const gridInnerSx: Record<GridInnerVariant, SxProps<Theme>> = {
	"csv-import-preview": {
		height: "100%"
	},
	dock: {
		display: "flex",
		flex: 1,
		flexDirection: "column",
		minHeight: 0,
		position: "relative",
		width: "100%",
		zIndex: 1
	},
	"fit-height": {
		height: "100%"
	},
	"flex-fill": {
		height: "100%"
	},
	"header-title": {
		height: "100%"
	},
	"kit-building-kits": {
		height: "100%"
	},
	"kit-building-skus": {
		height: "100%"
	},
	"mass-update": {
		height: "100%"
	},
	modal: {
		height: "calc(100% - 75px)"
	},
	"modal-stepper": {
		height: "calc(100% - 96px)"
	},
	"modal-tab": {
		height: "100%"
	},
	normal: {
		display: "block",
		height: "calc(100% - 81px)",
		position: "relative",
		width: "100%",
		zIndex: 1
	},
	"recipe-details": {
		height: "100%"
	},
	"card-tab": {
		height: "100%"
	},
	"card-tab-header": {
		height: "100%"
	},
	"restock-loadlist": {
		height: "calc(100% - 85px)"
	},
	"settings-page": {
		display: "flex",
		flex: 1,
		flexDirection: "column",
		minHeight: 0,
		position: "relative",
		width: "100%",
		zIndex: 1
	},
	"staging-flow": {
		display: "block",
		height: "calc(100% - 75px)",
		position: "relative",
		width: "100%",
		zIndex: 1
	},
	"tab-card": {
		height: "100%"
	},
	tab: {
		height: "calc(100% - 75px)"
	}
};

export const gridFullscreenInnerSx: SxProps<Theme> = {
	height: "calc(100vh - 96px)"
};

export const gridWithMruSx: SxProps<Theme> = {
	height: "calc(100% - 121px)"
};

export const gridFullscreenWithMruSx: SxProps<Theme> = {
	height: "calc(100vh - 136px)"
};
